import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { notify } from "../../../components/Toast";
import CustomButton from "../../../components/CustomButton";
import { images } from "../../../config/config";
import { resetPassword } from "../../../services/auth";
import "./ResetPassword.css";

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const email = searchParams.get("email");
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        password: "",
        password_confirmation: ""
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const togglePassword = () => setShowPassword(!showPassword);
    const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
        // Clear error when user starts typing
        if (errors[e.target.name]) {
            setErrors((prev) => ({
                ...prev,
                [e.target.name]: ""
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        const newErrors = {};

        if (!token || !email) {
            notify.error("Invalid password reset link.");
            return;
        }

        if (!formData.password || formData.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters long.";
        }

        if (!formData.password_confirmation) {
            newErrors.password_confirmation = "Please confirm your password.";
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = "Passwords do not match.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        try {
            console.log("Reset password data:", {
                token,
                email,
                new_password: formData.password,
                password_confirmation: formData.password_confirmation,
                role: "client",
            });
            
            // Try resetting as client first
            let response = await resetPassword({
                token,
                email,
                new_password: formData.password,
                password_confirmation: formData.password_confirmation,
                role: "client",
            });

            console.log("Reset password response:", response);
            console.log("Response data:", response.data);

            // If client reset fails, try as admin
            if (!response.success) {
                console.log("Client reset failed, trying admin...");
                response = await resetPassword({
                    token,
                    email,
                    new_password: formData.password,
                    password_confirmation: formData.password_confirmation,
                    role: "admin",
                });
                console.log("Admin reset response:", response);
            }

            if (response.success) {
                notify.success(response.message);
                setTimeout(() => {
                    navigate("/");
                }, 2000);
            } else {
                notify.error(response.message || "Failed to reset password.");
            }
        } catch (err) {
            console.error("Reset password error:", err);
            console.error("Error response:", err?.response);
            notify.error(err?.response?.data?.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container d-flex justify-content-center align-items-center min-vh-100">
            <div className="login-box">
                <img
                    src={images.logo}
                    alt="Recomp+"
                    className="login-logo"
                />
                <h3 className="text-center login-title">Reset Your Password</h3>
                <p className="text-center text-muted mb-4">Enter your new password below.</p>

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label htmlFor="password" className="form-label login-label">New Password</label>
                        <div className="input-group">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`form-control login-input ${errors.password ? 'is-invalid' : ''}`}
                                id="password"
                                placeholder="Enter new password"
                            />
                            <span
                                className="input-group-text password-toggle-icon"
                                onClick={togglePassword}
                                style={{ cursor: "pointer" }}
                            >
                                {showPassword ? (
                                    <i className="bi bi-eye-slash"></i>
                                ) : (
                                    <i className="bi bi-eye"></i>
                                )}
                            </span>
                        </div>
                        {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                    </div>

                    <div className="mb-3">
                        <label htmlFor="password_confirmation" className="form-label login-label">Confirm Password</label>
                        <div className="input-group">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="password_confirmation"
                                value={formData.password_confirmation}
                                onChange={handleChange}
                                className={`form-control login-input ${errors.password_confirmation ? 'is-invalid' : ''}`}
                                id="password_confirmation"
                                placeholder="Confirm new password"
                            />
                            <span
                                className="input-group-text password-toggle-icon"
                                onClick={toggleConfirmPassword}
                                style={{ cursor: "pointer" }}
                            >
                                {showConfirmPassword ? (
                                    <i className="bi bi-eye-slash"></i>
                                ) : (
                                    <i className="bi bi-eye"></i>
                                )}
                            </span>
                        </div>
                        {errors.password_confirmation && <div className="invalid-feedback d-block">{errors.password_confirmation}</div>}
                    </div>

                    <CustomButton
                        className="w-100"
                        loading={loading}
                        type="submit"
                    >
                        Reset Password
                    </CustomButton>
                </form>

                <div className="mt-3 text-center">
                    <button
                        type="button"
                        className="btn btn-link text-decoration-none"
                        onClick={() => navigate("/")}
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
